import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { VerificacionMarkup } from '../main-rates/markup-check';

/**
 * Aviso al operador de que la tasa Aguacate se pasó del umbral de markup.
 *
 * Lo abre la barra de navegación, no una pantalla, así que aparece esté donde
 * esté el operador. No se cierra con la tecla de escape ni tocando afuera: el
 * único camino de salida es reconocer que se escaló, o que el markup vuelva a
 * la banda —en cuyo caso lo cierra la barra sola.
 *
 * El escalamiento en sí ocurre fuera de la plataforma: el operador avisa al
 * encargado de la tasa por su cuenta. Acá solo se le muestran los números que
 * tiene que transmitir y se registra que dice haberlo hecho.
 */
@Component({
  selector: 'app-markup-alert-modal',
  template: `
    <div style="padding: 28px 28px 24px; text-align: center; max-width: 420px;">
      <div
        style="width: 54px; height: 54px; border-radius: 50%; background: #fee2e2; color: #dc2626; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;"
      >
        <mat-icon style="font-size: 28px; width: 28px; height: 28px;">warning</mat-icon>
      </div>

      <h2 style="font-size: 1.3rem; font-weight: 800; color: #111827; margin: 0 0 8px;">
        Tasa Aguacate sobre el umbral
      </h2>
      <p style="font-size: 0.95rem; color: #4b5563; margin: 0 0 20px; line-height: 1.5;">
        El markup superó el máximo configurado. Avisá al encargado de la tasa
        para que aplique los correctivos.
      </p>

      <div style="display: flex; gap: 10px; margin-bottom: 20px;">
        <div
          style="flex: 1; background: #f0fdf4; border: 1px solid #dcfce7; border-radius: 12px; padding: 10px 8px;"
        >
          <span style="display: block; font-size: 0.68rem; color: #6b7280; font-weight: 600; margin-bottom: 3px;">
            Spot
          </span>
          <b style="font-size: 0.95rem; font-family: monospace; color: #111827;">
            {{ data.verificacion.spot | number: '1.2-2' }}
          </b>
        </div>
        <div
          style="flex: 1; background: #f0fdf4; border: 1px solid #dcfce7; border-radius: 12px; padding: 10px 8px;"
        >
          <span style="display: block; font-size: 0.68rem; color: #6b7280; font-weight: 600; margin-bottom: 3px;">
            Aguacate
          </span>
          <b style="font-size: 0.95rem; font-family: monospace; color: #111827;">
            {{ data.verificacion.aguacate | number: '1.2-2' }}
          </b>
        </div>
        <div
          style="flex: 1; background: #fef2f2; border: 1px solid #fecaca; border-radius: 12px; padding: 10px 8px;"
        >
          <span style="display: block; font-size: 0.68rem; color: #6b7280; font-weight: 600; margin-bottom: 3px;">
            Markup
          </span>
          <b style="font-size: 0.95rem; font-family: monospace; color: #dc2626;">
            {{ data.verificacion.markupPct >= 0 ? '+' : ''
            }}{{ data.verificacion.markupPct | number: '1.2-2' }}%
          </b>
        </div>
      </div>

      <button
        type="button"
        (click)="copiarMensaje()"
        style="width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px; background: white; color: #374151; border: 1px solid #d1d5db; border-radius: 12px; padding: 13px; font-size: 0.9rem; font-weight: 600; cursor: pointer; margin-bottom: 10px;"
      >
        <mat-icon style="font-size: 18px; width: 18px; height: 18px;">
          {{ copiado ? 'check' : 'content_copy' }}
        </mat-icon>
        {{ copiado ? 'Mensaje copiado' : 'Copiar mensaje para el encargado' }}
      </button>

      <button
        type="button"
        (click)="reconocer()"
        style="width: 100%; background: #1C593D; color: white; border: none; border-radius: 12px; padding: 14px; font-size: 0.95rem; font-weight: 700; cursor: pointer; transition: background 0.15s ease;"
        onmouseover="this.style.background='#059669'"
        onmouseout="this.style.background='#1C593D'"
      >
        Ya avisé al encargado de la tasa
      </button>

      <p *ngIf="data.operador" style="font-size: 0.78rem; color: #6b7280; margin: 14px 0 0;">
        Operador de la tasa:
        <b style="font-family: monospace; color: #374151;">{{ data.operador }}</b>
      </p>

      <p style="font-size: 0.78rem; color: #9ca3af; margin: 8px 0 0;">
        Si en {{ minutosParaVolver }} min sigue sobre el umbral, este aviso vuelve a aparecer.
      </p>
    </div>
  `,
})
export class MarkupAlertModalComponent {
  constructor(
    public dialogRef: MatDialogRef<MarkupAlertModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      verificacion: VerificacionMarkup;
      operador: string | null;
      actualizadoEn: Date;
      minutosParaVolver: number;
    },
    private clipboard: Clipboard,
  ) {}

  copiado = false;

  /**
   * Arma el mensaje y lo deja en el portapapeles. El operador lo pega en su
   * Telegram o su WhatsApp y se lo manda al encargado: la plataforma no envía
   * nada, solo evita que tenga que transcribir seis números a mano.
   */
  copiarMensaje(): void {
    this.clipboard.copy(this.mensaje());
    this.copiado = true;
    setTimeout(() => (this.copiado = false), 2500);
  }

  private mensaje(): string {
    const v = this.data.verificacion;
    const signo = v.markupPct >= 0 ? '+' : '';
    const cuando = this.data.actualizadoEn.toLocaleString('es-CL', {
      day: 'numeric',
      month: 'numeric',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    return [
      '🔴 Markup Aguacate sobre umbral',
      'El ratio aguacate/spot superó el máximo configurado.',
      '',
      `Spot: ${this.n(v.spot)} · Aguacate: ${this.n(v.aguacate)}`,
      `Ratio: ${this.n(v.ratio, 5)} (umbral ${this.n(v.umbral, 3)})`,
      `Markup: ${signo}${this.n(v.markupPct)}%`,
      `Operador: ${this.data.operador || 'sin definir'} · ${cuando}`,
    ].join('\n');
  }

  private n(valor: number, decimales = 2): string {
    return valor.toLocaleString('es-CL', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  }

  get minutosParaVolver(): number {
    return this.data.minutosParaVolver;
  }

  reconocer(): void {
    this.dialogRef.close('ack');
  }
}
